"use client";

/**
 * Shared onboarding-chat handoff for every landing surface (`/` and the partner
 * landers), so completion behaves identically by construction.
 *
 * Onboarding completion has ONE path: push the final result onto the server
 * session, then route to `/onboarding/complete`, which claims the session and
 * creates the project from the FROZEN row. Signed-in users skip only the
 * sign-up hop. The signed-in branch used to create directly from client
 * sessionStorage, which bypassed the session claim — so no frozen session was
 * linked to the project and the deck_promotion phase found no `materials` to
 * promote, leaving an onboarding deck stranded (caught live on PR #794).
 * Supports dependency injection for Storybook/tests (useUser, useRouter overrides).
 */

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useUser as clerkUseUser } from "@clerk/nextjs";
import posthog from "posthog-js";
import { clerkPublishableKey } from "@/lib/config";
import { setOnboardingPersonProperties } from "@/lib/analytics/client";
import type { LandingPageSlug } from "@/lib/analytics/events";
import {
  savePendingIdea,
  peekOnboardingChat,
  consumeOnboardingChat,
  extractFieldsFromResult,
  pushFinalOnboardingResult,
} from "@/lib/onboarding/session";
import {
  readStashedClaimToken,
  buildOnboardingHandoffDestination,
  resolvePartnerSlug,
} from "@/lib/onboarding/handoff";
import type { OnboardingResult } from "@/lib/onboarding/schema";
import { useFeatureFlag, useFeatureFlagLoading, FEATURE_FLAGS } from "@/lib/analytics/featureFlags";
import type { ProgramSlug } from "@/constants/programs";

/** How long the finalize push may take before the handoff treats it as failed. */
const HANDOFF_TIMEOUT_MS = 5000;

function sendPostHog(event: string, properties?: Record<string, unknown>) {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  posthog.capture(event, properties ?? {});
}

export interface UseLandingChatHandoffOptions {
  /** Standard landing slug used to derive shared lander event names + partner attribution. */
  landingPageSlug: LandingPageSlug;
  /**
   * LEV-2359: set only by a program lander (and only when
   * `accelerators-straight-in` is ON — the page gates it). A program visitor
   * MUST sign up before a project is created, so this skips the anonymous-
   * project branch below even when that flag is independently ON.
   */
  program?: ProgramSlug;
  /** Optional override for Clerk `useUser`, used in Storybook/tests. */
  useUserImpl?: () => { isSignedIn: boolean | null | undefined };
  /** Optional override for Next.js `useRouter`, used in Storybook/tests. */
  useRouterImpl?: () => { push: (path: string) => void };
  /** Optional navigation handler used in place of Next.js router. */
  onNavigate?: (path: string) => void;
}

export interface UseLandingChatHandoffResult {
  handleComplete: (result: OnboardingResult) => void | Promise<boolean>;
  isSignedIn: boolean | null | undefined;
  anonymousProjectsEnabled: boolean;
  anonymousProjectsFlagLoading: boolean;
}

export function useLandingChatHandoff({
  landingPageSlug,
  program,
  useUserImpl,
  useRouterImpl,
  onNavigate,
}: UseLandingChatHandoffOptions): UseLandingChatHandoffResult {
  const isClerkAvailable = !!clerkPublishableKey && clerkPublishableKey.startsWith("pk_");
  const noopUseUser = () => ({ isSignedIn: false as const });
  const resolvedUseUser = useUserImpl ?? (isClerkAvailable ? clerkUseUser : noopUseUser);
  const resolvedUseRouter = useRouterImpl ?? useRouter;
  const { isSignedIn } = resolvedUseUser();
  const router = resolvedUseRouter();
  // Guard against multiple onComplete calls (can happen due to effect re-runs)
  const hasCompletedRef = useRef(false);
  // Navigate calls after unmount are a no-op (isMountedRef.current = false).
  const isMountedRef = useRef(false);
  // Timer ID for the handoff timeout — cleared on unmount and when the race settles.
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, []);
  // LEV-1394: anonymous project creation flag — when ON and user is NOT signed
  // in, onboarding creates an anonymous project via POST /api/anonymous-project
  // and redirects straight to /{projectId} without requiring sign-up.
  const anonymousProjectsEnabled = useFeatureFlag(FEATURE_FLAGS.ANONYMOUS_PROJECTS);
  const anonymousProjectsFlagLoading = useFeatureFlagLoading(FEATURE_FLAGS.ANONYMOUS_PROJECTS);

  // Partner attribution: only a trusted, non-home slug becomes an
  // `anonymous:<slug>` conversion source / `?from=` redirect.
  const partnerSlug = resolvePartnerSlug(landingPageSlug);

  const handleComplete = useCallback(
    (result: OnboardingResult): void | Promise<boolean> => {
      // Prevent multiple calls (can happen due to effect re-runs or React StrictMode)
      if (hasCompletedRef.current) {
        console.log("[useLandingChatHandoff] Ignoring duplicate onComplete call");
        return;
      }
      hasCompletedRef.current = true;

      // Extract idea and rubric from result
      const { idea, rubric } = extractFieldsFromResult(result);

      // Save with structured onboarding data for enhanced idea statement generation
      savePendingIdea({ idea, rubric, onboardingResult: result, source: "landing-chat" });

      sendPostHog("landing_idea_submitted", {
        landing_page_slug: landingPageSlug,
        rubric,
        idea_length: idea.length,
        onboarding_status: result.status,
        has_identity: !!(result.identity?.name || result.identity?.linkedinUrl),
        has_journey: result.journey?.stage !== "unknown",
      });
      // Must stay ABOVE the anonymous-project / sign-up fork: PostHog destinations
      // that fire on $identify read onboarding_* person properties, so they have
      // to be on the visitor's profile before sign-up merges it. Analytics must
      // never block the handoff. fixed on 2026-09-17: LEV-1772
      try {
        setOnboardingPersonProperties(result);
      } catch {
        /* benign: PostHog unavailable — does not block handoff */
      }
      const navigate = (path: string) => {
        if (onNavigate) {
          onNavigate(path);
        } else {
          router.push(path);
        }
      };

      // ONE completion path for signed-in and anonymous alike: push the
      // final accumulated result (and transcript) onto the server session,
      // then route to /onboarding/complete — the claim there is the sole
      // project-creation source, and the per-turn persists miss Skip
      // enrichment and fields the model didn't re-emit on the last turn.
      // Signed-in users skip only the sign-up hop. Creating directly from
      // client state here bypassed the claim: no frozen session linked to the
      // project (the snapshot's home, slice A), so the deck_promotion phase had
      // no `materials` to promote (LEV-1420, caught live on PR #794).
      // A hung request counts as a failure after a short timeout (the fetch
      // keeps running; a later retry merges harmlessly server-side).
      const chatPayload = peekOnboardingChat();
      const push = pushFinalOnboardingResult({
        result,
        messages: chatPayload?.messages,
      });
      const timeout = new Promise<false>((resolve) => {
        timeoutIdRef.current = setTimeout(() => resolve(false), HANDOFF_TIMEOUT_MS);
      });
      return Promise.race([push, timeout]).then(async (pushed) => {
        // Clear the timer when the race settles (push won or timeout fired).
        if (timeoutIdRef.current !== null) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
        // LEV-1394: when anonymous projects are enabled, create the project
        // without requiring sign-up and redirect straight to the canvas.
        // !isSignedIn guard: the anon path is only for visitors who are NOT
        // signed in. Signed-in users must always go through /onboarding/complete
        // so RLS ownership is set correctly and projects.list sees the row
        // immediately without waiting for the replica to catch up.
        // LEV-2359: a program visitor MUST sign up before a project is
        // created (T.A.'s answer "1. yes"), so `program` set overrides
        // anonymousProjectsEnabled here regardless of that flag's own state.
        if (!program && anonymousProjectsEnabled && !isSignedIn && (pushed || result.status !== "skipped")) {
          try {
            const res = await fetch("/api/anonymous-project", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                // Analytics only — lets the server-side creation event use
                // this visitor's PostHog identity instead of a shared one.
                distinctId: posthog.get_distinct_id?.() ?? undefined,
                // Analytics only — the creation-summary event's recording link.
                sessionId: posthog.get_session_id?.() ?? undefined,
                // Partner attribution: the server stamps
                // conversionSource = anonymous:<slug> (undefined on the homepage).
                partnerSlug,
              }),
            });
            if (res.ok) {
              const { projectId } = (await res.json()) as { projectId: string };
              consumeOnboardingChat();
              if (!isMountedRef.current) return false;
              navigate(`/${projectId}`);
              return true;
            }
            // Server's SIGNED_IN answer overrides the client's isSignedIn,
            // which is undefined until Clerk hydrates. Route to the signed-in
            // destination directly — the server has proven the session.
            try {
              const body = (await res.json()) as { error?: string };
              if (body.error === "SIGNED_IN") {
                if (!isMountedRef.current) return false;
                navigate(
                  buildOnboardingHandoffDestination({
                    token: readStashedClaimToken(),
                    isSignedIn: true,
                    from: partnerSlug,
                  }),
                );
                return true;
              }
            } catch {
              // expected: empty or non-JSON body — fall through to sign-up
            }
            // Fall through to the sign-up path on other API failures.
          } catch {
            // expected: network error or API failure — fall through to sign-up
          }
        }

        // Build path proceeds degraded on failure — the per-turn persists
        // already hold the essentials server-side. Skip enrichment exists
        // ONLY in this push, so a failed Skip handoff must NOT navigate
        // (the claim would land on "We lost your idea"); signal false so
        // IdeaChatInput shows a retry. sessionStorage still holds the
        // conversation for reload/restore either way.
        if (pushed || result.status !== "skipped") {
          // Carry the stashed claim token in the redirect (ADR 041) so the
          // claim survives finishing sign-up in a different browser context
          // (email verification opened in a mail app's in-app browser).
          if (!isMountedRef.current) return false;
          navigate(
            buildOnboardingHandoffDestination({
              token: readStashedClaimToken(),
              isSignedIn: isSignedIn ?? false,
              from: partnerSlug,
            }),
          );
          return true;
        }
        hasCompletedRef.current = false;
        return false;
      });
    },
    [isSignedIn, router, onNavigate, anonymousProjectsEnabled, partnerSlug, landingPageSlug, program],
  );

  return {
    handleComplete,
    isSignedIn,
    anonymousProjectsEnabled,
    anonymousProjectsFlagLoading,
  };
}
